import React, { type MouseEvent } from 'react';
import { flexRender, type Cell, type Row } from '@tanstack/react-table';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useResourceKit } from '@resource-kit/core/react';
import type {
  ListCardItem,
  ListCardLayout,
  ListCardRenderer,
} from '@resource-kit/controllers/react';
import { Checkbox } from '../primitives/Checkbox';
import { cn } from '../../cn';
import { columnLabel, isMechanismColumn } from './table-export';

/**
 * The default card body (#2680): each item as "label value", wrapping onto
 * new lines, with long values breaking instead of widening the card. Exported
 * so a custom card (`ResourceHost.listCard`) can lay out the items it keeps
 * the same way.
 */
export function ListCardFields({ items }: { items: readonly ListCardItem[] }) {
  if (items.length === 0) return null;
  return (
    <dl className="flex flex-wrap gap-x-4 gap-y-1.5">
      {items.map((item) => (
        <div key={item.id} className="flex min-w-0 max-w-full items-baseline gap-1.5">
          <dt className="shrink-0 text-xs text-muted-foreground">{item.label}</dt>
          <dd className="min-w-0 break-words">{item.content}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Clicks that land on one of these inside the title row belong to that
 * control, not to the row. Cell content can carry its own link (an
 * `EntityLink`) or button; most stop propagation themselves, this is the
 * second line for the ones that do not.
 */
const INTERACTIVE_SELECTOR =
  'a,button,input,select,textarea,label,[role="checkbox"],[role="button"],[role="switch"],[role="menuitem"],[role="combobox"]';

/**
 * Props of one card. Every input that can change what the card paints is an
 * explicit prop, as for the memoized table row (see `DataTableRowProps` in
 * `DataTable`): `cells` carries column visibility, `language` is a memo key
 * for cell renderers that translate through the i18n singleton.
 */
interface DataTableCardProps<TData> {
  row: Row<TData>;
  cells: Cell<TData, unknown>[];
  isSelected: boolean;
  selectable: boolean;
  /** Where the title row goes; wins over `onOpen`. */
  href?: string;
  /** Stable identity — the table's latest-ref row click. */
  onOpen?: (row: TData) => void;
  renderCard?: ListCardRenderer<TData>;
  selectRowLabel: string;
  language: string;
}

function DataTableCardImpl<TData>({
  row,
  cells,
  isSelected,
  selectable,
  href,
  onOpen,
  renderCard,
  selectRowLabel,
}: DataTableCardProps<TData>) {
  const { t } = useResourceKit();
  const navigate = useNavigate();

  // Mechanism columns (row actions) are framework controls, not data: they
  // are neither the title nor a field, and sit at the end of the title row.
  const mechanismCells = cells.filter((cell) => isMechanismColumn(cell.column.columnDef));
  const items: ListCardItem[] = cells
    .filter((cell) => !isMechanismColumn(cell.column.columnDef))
    .map((cell) => ({
      id: cell.column.id,
      label: columnLabel(cell.column.columnDef, t),
      content: flexRender(cell.column.columnDef.cell, cell.getContext()),
    }));

  const layout: ListCardLayout = renderCard
    ? renderCard({ row: row.original, items })
    : {
        title: items[0]?.content,
        body: items.length > 1 ? <ListCardFields items={items.slice(1)} /> : null,
      };

  const canOpen = href !== undefined || onOpen !== undefined;
  const open = () => {
    if (href !== undefined) navigate(href);
    else onOpen?.(row.original);
  };
  const handleTitleClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target;
    // A host cell in the title row can render a React portal (a popover, a
    // dialog). A click inside it bubbles through the React tree to here but is
    // not inside this row in the DOM: it is not a tap on the row. (Row-action
    // menu clicks never get here: the mechanism wrapper below and
    // RowActionsCell stop them.)
    if (!(target instanceof Node) || !event.currentTarget.contains(target)) return;
    if (target instanceof Element) {
      const control = target.closest(INTERACTIVE_SELECTOR);
      if (control !== null && event.currentTarget.contains(control)) return;
    }
    open();
  };

  const openLabel = t('rk.table.openRecord');
  const openClassName = 'inline-flex size-11 shrink-0 items-center justify-center';

  return (
    <article
      data-testid="list-card"
      data-state={isSelected ? 'selected' : undefined}
      className="rounded-md border bg-card text-sm"
    >
      {/* Not a link and not a button: the title can hold its own link. */}
      <div
        data-testid="list-card-title"
        className={cn('flex min-h-11 items-center gap-2 px-3 py-2', canOpen && 'cursor-pointer')}
        onClick={canOpen ? handleTitleClick : undefined}
      >
        {selectable && (
          // A 44px hit area around the 16px box: a tap anywhere in it toggles
          // the row and never reaches the title row. A tap ON the checkbox is
          // toggled by the checkbox itself (its click is re-dispatched to the
          // hidden input inside this wrapper), so only the wrapper's own
          // clicks toggle here.
          <span
            className="inline-flex size-11 shrink-0 items-center justify-center"
            onClick={(event) => {
              event.stopPropagation();
              if (event.target === event.currentTarget) row.toggleSelected(!isSelected);
            }}
          >
            <Checkbox
              checked={isSelected}
              onCheckedChange={(value) => row.toggleSelected(!!value)}
              aria-label={selectRowLabel}
            />
          </span>
        )}
        <div className="min-w-0 flex-1">{layout.title}</div>
        {mechanismCells.map((cell) => (
          // A 44px hit area whose taps never open the record, and a 44px
          // row-actions trigger inside it (the table keeps its compact
          // trigger). Only the trigger grows: the cell also renders the
          // action's confirm modal, whose buttons keep their own size.
          // Collapses when the cell renders nothing (no visible action).
          <span
            key={cell.id}
            className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center empty:hidden [&_[data-slot=row-actions-trigger]]:size-11"
            onClick={(event) => event.stopPropagation()}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </span>
        ))}
        {href !== undefined ? (
          <Link
            to={href}
            aria-label={openLabel}
            className={openClassName}
            onClick={(event) => event.stopPropagation()}
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        ) : onOpen !== undefined ? (
          <button
            type="button"
            aria-label={openLabel}
            className={openClassName}
            onClick={(event) => {
              event.stopPropagation();
              onOpen(row.original);
            }}
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {layout.body != null && <div className="border-t px-3 py-2">{layout.body}</div>}
    </article>
  );
}

/**
 * One card of the `DataTable` card layout (#2680). Published only because
 * `DataTable` imports it across files and `./react` re-exports this module
 * whole; a host renders `DataTable` (or `ListView`), not this.
 *
 * @internal
 */
export const DataTableCard = React.memo(DataTableCardImpl) as typeof DataTableCardImpl;
